import type { FC, KeyboardEvent, ReactNode } from 'react'
import type { ChatItem, FullServiceTab } from '@/constants/home'

import Image from 'next/image'
import { useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { PillButton } from '@/components/generals/pill-button'
import { useContact } from '@/components/generals/contact-dialog'
import { useInView } from '@/hooks/use-in-view'
import { useReducedMotion } from '@/hooks/use-reduced-motion'
import { fullServiceTabs } from '@/constants/home'

import {
    ArrowUp,
    CalendarDays,
    Check,
    KeyRound,
    LoaderCircle,
    TrendingDown
} from 'lucide-react'

const noticeIcons = {
    key: KeyRound,
    calendar: CalendarDays,
    trend: TrendingDown
}

type Typing = 'agent' | 'customer' | null

const bubble = 'rounded-[18px] bg-gradient-to-b from-[#46464d] to-[#38383e] px-4 py-3 text-[14px] font-medium leading-relaxed text-white shadow-[0_1px_0_rgba(255,255,255,0.08)_inset,0_8px_18px_-12px_rgba(0,0,0,0.8)] ring-1 ring-white/10'

const TypingBubble: FC<{ side: 'agent' | 'customer' }> = ({ side }): ReactNode => (
    <div className={cn('fc-fade-up flex', side === 'customer' ? 'justify-end' : 'justify-start')}>
        <div className={cn(bubble, 'inline-flex items-center gap-1 py-4')}>
            {[0, 1, 2].map(index => (
                <span
                    key={index}
                    className='size-1.5 rounded-full bg-white/60 animate-typing-dot'
                    style={{ animationDelay: `${index * 160}ms` }}
                />
            ))}
        </div>
    </div>
)

const ChatLine: FC<{ item: ChatItem, done: boolean }> = ({ item, done }): ReactNode => {
    if (item.kind === 'customer') {
        return (
            <div className='fc-pop-in flex justify-end'>
                <p className={cn(bubble, 'max-w-[86%]')}>
                    {item.text}
                </p>
            </div>
        )
    }

    if (item.kind === 'status') {
        return (
            <div className='fc-pop-in flex justify-center'>
                <span className='inline-flex items-center gap-1.5 rounded-full bg-mint/15 px-3 py-1 text-[12px] font-semibold text-mint ring-1 ring-mint/25'>
                    <Check className='size-3.5' aria-hidden='true' />
                    {item.text}
                </span>
            </div>
        )
    }

    if (item.kind === 'task') {
        return (
            <div className={cn(bubble, 'fc-pop-in flex items-center gap-3')}>
                <span className={cn('inline-flex size-6 shrink-0 items-center justify-center rounded-full transition-colors duration-300', done ? 'bg-mint text-forest' : 'bg-white/10 text-white/70')}>
                    {done ? <Check className='size-3.5' aria-hidden='true' /> : <LoaderCircle className='size-3.5 animate-spin' aria-hidden='true' />}
                </span>

                <span>
                    {item.text}
                </span>
            </div>
        )
    }

    if (item.kind === 'notice') {
        const Icon = noticeIcons[item.icon]

        return (
            <div className='fc-pop-in flex justify-start'>
                <div className={cn(bubble, 'flex max-w-[92%] items-start gap-3')}>
                    <span className='mt-0.5 inline-flex size-6 shrink-0 items-center justify-center rounded-full bg-white/10 text-[#c9b9ff]'>
                        <Icon className='size-3.5' aria-hidden='true' />
                    </span>

                    <p>
                        {item.text}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className='fc-pop-in flex justify-start'>
            <p className={cn(bubble, 'max-w-[86%]')}>
                {item.text}
            </p>
        </div>
    )
}

interface ChatCardProps {
    tab: FullServiceTab
    play: boolean
    reduced: boolean
}

// Plays one tab's conversation: a typing indicator, then each message in turn.
const ChatCard: FC<ChatCardProps> = ({ tab, play, reduced }): ReactNode => {
    const [shown, setShown] = useState(0)
    const [doneTasks, setDoneTasks] = useState(0)
    const [typing, setTyping] = useState<Typing>(null)
    const started = useRef(false)

    useEffect(() => {
        if (!play || started.current) return

        started.current = true

        if (reduced) {
            setShown(tab.chat.length)
            setDoneTasks(tab.chat.length)
            return
        }

        const timers: Array<number> = []
        let time = 450

        tab.chat.forEach((item, index) => {
            const speaker: Typing = item.kind === 'customer' ? 'customer' : item.kind === 'agent' || item.kind === 'notice' ? 'agent' : null
            const typingFor = speaker ? Math.min(650 + item.text.length * 9, 1500) : 250

            if (speaker) timers.push(window.setTimeout(() => setTyping(speaker), time))

            time += typingFor

            timers.push(window.setTimeout(() => {
                setTyping(null)
                setShown(index + 1)
            }, time))

            if (item.kind === 'task') timers.push(window.setTimeout(() => setDoneTasks(index + 1), time + 650))

            time += item.kind === 'task' ? 750 : 900
        })

        return () => timers.forEach(timer => window.clearTimeout(timer))
    }, [play, reduced, tab])

    return (
        <div className='flex flex-col gap-5'>
            <div className='flex items-center justify-center gap-3'>
                <Image
                    src={tab.person.image}
                    alt={tab.person.name}
                    width={40}
                    height={40}
                    className='size-10 shrink-0 rounded-full object-cover object-top ring-2 ring-white/15'
                />

                <span className='text-[15px] font-semibold text-white'>
                    {tab.person.name}
                </span>

                <span aria-hidden='true' className='text-white/30'>&middot;</span>

                <span className='text-[15px] text-white/55'>
                    {tab.person.role}
                </span>
            </div>

            <div className='flex h-[460px] flex-col overflow-hidden rounded-[26px] bg-[#28282d] text-white shadow-[0_40px_90px_-40px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,255,255,0.08)] sm:h-[500px]'>
                <div className='fc-fade-top flex flex-1 flex-col justify-end gap-3 overflow-hidden px-4 py-5 sm:px-5' aria-live='polite'>
                    {tab.chat.slice(0, shown).map((item, index) => (
                        <ChatLine key={`${tab.id}-${index}`} item={item} done={index < doneTasks} />
                    ))}

                    {typing && <TypingBubble side={typing} />}
                </div>

                <div className='px-2 pb-2'>
                    <div className='flex h-[92px] flex-col justify-between rounded-[22px] bg-[#1d1d21] px-4 pb-2 pt-3 text-[15px] text-white/40 ring-1 ring-white/[0.06]'>
                        <span>Start typing...</span>

                        <span aria-hidden='true' className='inline-flex size-9 items-center justify-center self-end rounded-full bg-[#4a4a51] text-white'>
                            <ArrowUp className='size-4' />
                        </span>
                    </div>
                </div>
            </div>
        </div>
    )
}

const PanelPattern: FC = (): ReactNode => (
    <div aria-hidden='true' className='pointer-events-none absolute inset-0 overflow-hidden'>
        <div className='absolute inset-x-0 top-6 h-px bg-white/[0.06]' />
        <div className='absolute inset-x-0 bottom-6 h-px bg-white/[0.06]' />
        <div className='absolute inset-y-0 left-[8%] w-px bg-[repeating-linear-gradient(to_bottom,rgba(255,255,255,0.09)_0_2px,transparent_2px_6px)]' />
        <div className='absolute inset-y-0 right-[8%] w-px bg-[repeating-linear-gradient(to_bottom,rgba(255,255,255,0.09)_0_2px,transparent_2px_6px)]' />

        <div className='absolute -right-32 -top-40 size-[620px] rounded-full bg-[radial-gradient(circle_at_center,rgba(114,65,255,0.32),transparent_62%)] blur-2xl' />
        <div className='absolute -bottom-48 -left-24 size-[520px] rounded-full bg-[radial-gradient(circle_at_center,rgba(197,241,216,0.12),transparent_60%)] blur-2xl' />

        <div className='absolute -bottom-[340px] -left-[220px] size-[680px]'>
            <div className='absolute inset-[170px] rounded-full border border-white/[0.07]' />
            <div className='absolute inset-0 rounded-full border border-white/[0.05]' />

            <div className='absolute inset-[170px] animate-spin-slow' style={{ animationDuration: '40s' }}>
                <span className='absolute left-1/2 top-0 size-2 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#b6a6ff] shadow-[0_0_14px_rgba(182,166,255,0.9)]' />
            </div>

            <div className='absolute inset-0 animate-spin-slow' style={{ animationDuration: '80s', animationDirection: 'reverse' }}>
                <span className='absolute bottom-[14%] right-[14%] size-1.5 rounded-full bg-mint shadow-[0_0_12px_rgba(197,241,216,0.9)]' />
            </div>
        </div>
    </div>
)

export const FullServiceSection: FC = (): ReactNode => {
    const { openContact } = useContact()
    const reduced = useReducedMotion()
    const { ref, inView } = useInView<HTMLElement>({ once: false, rootMargin: '0px', threshold: 0.2 })
    const [active, setActive] = useState(0)
    const [cycle, setCycle] = useState(0)
    const [hovered, setHovered] = useState(false)
    const [seen, setSeen] = useState(false)
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([])
    const tab = fullServiceTabs[active]
    const paused = !inView || hovered || reduced

    useEffect(() => {
        if (inView) setSeen(true)
    }, [inView])

    const select = (index: number) => {
        setActive(index)
        setCycle(previous => previous + 1)
    }

    const handleKeys = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return

        event.preventDefault()

        const direction = event.key === 'ArrowRight' ? 1 : -1
        const next = (active + direction + fullServiceTabs.length) % fullServiceTabs.length

        select(next)
        tabRefs.current[next]?.focus()
    }

    return (
        <section ref={ref} id='full-service' aria-labelledby='full-service-title' className='px-3 sm:px-4 lg:px-6'>
            <div className='relative overflow-hidden rounded-[28px] bg-night text-white lg:rounded-[40px]'>
                <PanelPattern />

                <div className='fc-container relative grid gap-12 py-20 md:py-24 lg:grid-cols-[minmax(0,1fr)_minmax(0,480px)] lg:grid-rows-[auto_1fr] lg:gap-x-20 lg:gap-y-16 lg:py-32'>
                    <Reveal className='flex max-w-[520px] flex-col items-start gap-6 lg:col-start-1 lg:row-start-1'>
                        <p className='text-[14px] font-medium text-[#c9c9d2]'>
                            Full service
                        </p>

                        <h2 id='full-service-title' className='text-[clamp(2.25rem,4.6vw,4rem)] font-medium leading-[1.04] tracking-[-0.04em]'>
                            <span className='block text-white'>Integrations are hard,</span>
                            <span className='fc-text-gradient-mint block pb-1'>let us help.</span>
                        </h2>

                        <p className='text-[16px] leading-relaxed text-[#b4b4be] md:text-[17px]'>
                            For teams that want expert guidance, FlowCraft pairs an enterprise-ready integration platform with a dedicated delivery crew that scopes, builds, launches and supports every production workflow.
                        </p>

                        <PillButton variant='inverse' chevron onClick={() => openContact({ intent: 'contact' })} className='mt-2'>
                            Talk to our team
                        </PillButton>
                    </Reveal>

                    <Reveal delay={120} className='flex flex-col gap-6 lg:col-start-1 lg:row-start-2 lg:self-end'>
                        <div role='tablist' aria-label='How full service works' onKeyDown={handleKeys} className='flex flex-wrap gap-x-7 gap-y-2 border-b border-white/[0.12]'>
                            {fullServiceTabs.map((item, index) => {
                                const isActive = index === active

                                return (
                                    <button
                                        key={item.id}
                                        ref={element => {
                                            tabRefs.current[index] = element
                                        }}
                                        type='button'
                                        role='tab'
                                        id={`full-service-tab-${item.id}`}
                                        aria-selected={isActive}
                                        aria-controls='full-service-panel'
                                        tabIndex={isActive ? 0 : -1}
                                        onClick={() => select(index)}
                                        className={cn(
                                            'relative -mb-px pb-4 text-[15px] font-medium tracking-[-0.01em] transition-colors duration-200 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white',
                                            isActive ? 'text-white [text-shadow:0_0_14px_rgba(255,255,255,0.25)]' : 'text-white/45 hover:text-white/80'
                                        )}
                                    >
                                        {item.label}

                                        <span aria-hidden='true' className='absolute inset-x-0 bottom-0 h-[2px] overflow-hidden rounded-full'>
                                            {isActive && (
                                                <span
                                                    key={cycle}
                                                    className='fc-progress-bar block h-full w-full rounded-full bg-gradient-to-r from-[#d7ccff] via-[#9d84ff] to-mint'
                                                    style={reduced
                                                        ? { animation: 'none', transform: 'scaleX(1)' }
                                                        : { animationDuration: `${item.duration}ms`, animationPlayState: paused ? 'paused' : 'running' }}
                                                    onAnimationEnd={() => select((index + 1) % fullServiceTabs.length)}
                                                />
                                            )}
                                        </span>
                                    </button>
                                )
                            })}
                        </div>

                        <p id='full-service-panel' role='tabpanel' aria-labelledby={`full-service-tab-${tab.id}`} className='max-w-[470px] text-[16px] leading-relaxed'>
                            <span key={`lead-${active}`} className='fc-fade-up text-white'>{tab.lead}</span>
                            <span key={`body-${active}`} className='fc-fade-up text-[#a8a8b3]'>{tab.body}</span>
                        </p>
                    </Reveal>

                    <Reveal
                        delay={200}
                        className='lg:col-start-2 lg:row-span-2 lg:row-start-1'
                    >
                        <div onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
                            <ChatCard key={`${tab.id}-${cycle}`} tab={tab} play={seen} reduced={reduced} />
                        </div>
                    </Reveal>
                </div>
            </div>
        </section>
    )
}
